import React, { CSSProperties, ReactNode } from 'react';
import { useAppSelector } from 'hooks';
import { Dayjs } from 'dayjs';

import { dateInputFieldFormat } from '~libs/dayjs';
import { Appointment, Client, Worker, WorkerAvailability } from '~weekly-planner/lib/common';

import Spinner from '~components/Spinner';

import { selectRowLoading } from '~weekly-planner/selectors/users';
import { selectWorkerLeaveStatus } from '~weekly-planner/selectors/users/leaveStatus';

import Cell from './Cell';

interface ComponentProps {
  headerCell: ReactNode;
  data: Worker | Client;
  days: Dayjs[];
  date: Dayjs;
  style?: CSSProperties;
  getAppointmentTitle: (appointment: Appointment) => string;
  isDashboardView?: boolean;
}

const Row: React.FC<ComponentProps> = ({
  headerCell,
  data,
  days,
  date,
  style,
  getAppointmentTitle,
  isDashboardView,
}) => {
  const loading = useAppSelector((state) => selectRowLoading(state, parseInt(data.id)));
  const isOnLeave = useAppSelector((state) => selectWorkerLeaveStatus(state, data.id, date));

  const getDayAvailability = (day: Dayjs) => {
    if (!('availability' in data) || !day || !data?.availability) return null;

    const dayName = day.format('dddd');
    return data?.availability[dayName as keyof WorkerAvailability] ?? {};
  };

  const isLoading = loading === 'pending';

  return (
    <div className="worker-row">
      {headerCell}
      {days.map((day, index) => (
        <Cell
          style={{ ...style, gridColumn: index + 2 }}
          key={`${data.id}-${index}`}
          id={`${data.id}-${index}`}
          events={data.events[day.format(dateInputFieldFormat)]}
          availability={getDayAvailability(day)}
          getAppointmentTitle={getAppointmentTitle}
          date={day.format(dateInputFieldFormat)}
          startDate={date.weekday(0)}
          isDashboardView={isDashboardView}
          isOnLeave={isOnLeave}
        />
      ))}
      {isLoading && (
        <div className="row-spinner-overlay" style={{ ...style, gridColumn: '2 / 9' }}>
          <Spinner loading={isLoading} />
        </div>
      )}
    </div>
  );
};

export default Row;
